import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { SiteSelector } from '@/components/SiteSelector';
import { UserProfileMenu } from '@/components/UserProfileMenu';
import { ClipboardList, FileText, MapPin, Shield, Package, ListChecks, Users, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { toast } from 'sonner';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, currentSiteId, setCurrentSiteId } = useAuth();
  const navigate = useNavigate();

  // Get user's roles for current site
  const { isReady: rolesReady, canManage, canManageUsers, hasPermission } = useSiteRoles(currentSiteId || undefined);

  // Calculate permissions
  // Everyone with a role can view instructions and stations
  const isRoleReady = rolesReady && !!currentSiteId;
  const hasRoleManagementAccess = isRoleReady && canManageUsers;
  const hasInstructionAccess = isRoleReady && hasPermission('core.instructions.view');
  const hasManagementAccess = isRoleReady && canManage;

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted">
      <header className="bg-card border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/favicon.ico"
              alt="Teijin Styling logo"
              className="h-10 w-10 rounded-lg shadow-sm"
            />
            <div>
              <h1 className="text-xl font-bold">{t('app.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('app.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SiteSelector
              selectedSiteId={currentSiteId || undefined}
              onSiteChange={setCurrentSiteId}
              showWhenSingle
            />
            <LanguageSwitcher />
            <UserProfileMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t('dashboard.welcome')}</h1>
            <p className="text-muted-foreground">{t('dashboard.selectFeature')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Operator Mode - Available to everyone */}
            <Card
              className="cursor-pointer hover:shadow-lg transition-all hover:border-primary"
              onClick={() => navigate('/operator')}
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <ClipboardList className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>{t('dashboard.operatorMode')}</CardTitle>
                <CardDescription>
                  {t('dashboard.operatorDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* View Instructions - Available to all roles */}
            <Card
              className={`transition-all ${
                hasInstructionAccess
                  ? 'cursor-pointer hover:shadow-lg hover:border-primary'
                  : 'opacity-60 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!isRoleReady) return;
                if (hasInstructionAccess) {
                  navigate('/manager/instructions');
                } else {
                  toast.error(t('dashboard.permissionAccessDenied'));
                }
              }}
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>{t('manager.manageInstructions')}</CardTitle>
                <CardDescription>
                  {t('manager.manageFullDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* View Stations - Available to all roles */}
            <Card
              className="cursor-pointer hover:shadow-lg transition-all hover:border-orange-500"
              onClick={() => navigate('/manager/stations')}
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-orange-500/10 flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-orange-500" />
                </div>
                <CardTitle>{t('manageStations.title')}</CardTitle>
                <CardDescription>
                  {t('manageStations.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* User Management - Requires site_manager or admin */}
            <Card
              className={`transition-all ${
                hasRoleManagementAccess
                  ? 'cursor-pointer hover:shadow-lg hover:border-purple-500'
                  : 'opacity-60 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!isRoleReady) return;
                if (hasRoleManagementAccess) {
                  navigate('/manager/user-management');
                } else {
                  toast.error(t('dashboard.permissionAccessDenied'));
                }
              }}
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4 relative">
                  <Shield className="h-6 w-6 text-purple-500" />
                  {!hasRoleManagementAccess && (
                    <div className="absolute -top-1 -right-1 bg-background rounded-full p-0.5">
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <CardTitle>{t('userManagement.title')}</CardTitle>
                <CardDescription>
                  {t('userManagement.subtitle')}
                  {!hasRoleManagementAccess && (
                    <span className="block text-xs text-destructive mt-1">
                       {t('dashboard.permissionRestricted')}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Import SAP - Requires site_manager or admin */}
            <Card
              className={`transition-all ${
                hasManagementAccess
                  ? 'cursor-pointer hover:shadow-lg hover:border-green-500'
                  : 'opacity-60 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!isRoleReady) return;
                if (hasManagementAccess) {
                  navigate('/manager/import-sap');
                } else {
                  toast.error(t('dashboard.permissionAccessDenied'));
                }
              }}
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 relative">
                  <Package className="h-6 w-6 text-green-500" />
                  {!hasManagementAccess && (
                    <div className="absolute -top-1 -right-1 bg-background rounded-full p-0.5">
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <CardTitle>{t('importSAP.title')}</CardTitle>
                <CardDescription>
                  {t('importSAP.subtitle')}
                  {!hasManagementAccess && (
                    <span className="block text-xs text-destructive mt-1">
                       {t('dashboard.permissionRestricted')}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Work Orders - Requires site_manager or admin */}
            <Card
              className={`transition-all ${
                hasManagementAccess
                  ? 'cursor-pointer hover:shadow-lg hover:border-blue-500'
                  : 'opacity-60 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!isRoleReady) return;
                if (hasManagementAccess) {
                  navigate('/manager/work-orders');
                } else {
                  toast.error(t('dashboard.permissionAccessDenied'));
                }
              }}
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4 relative">
                  <ListChecks className="h-6 w-6 text-blue-500" />
                  {!hasManagementAccess && (
                    <div className="absolute -top-1 -right-1 bg-background rounded-full p-0.5">
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <CardTitle>{t('carsManagement.title')}</CardTitle>
                <CardDescription>
                  {t('carsManagement.subtitle')}
                  {!hasManagementAccess && (
                    <span className="block text-xs text-destructive mt-1">
                       {t('dashboard.permissionRestricted')}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Help card */}
          <Card className="bg-muted/50 border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('dashboard.needAccess')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('dashboard.contactAdmin')} <code className="text-xs bg-background px-2 py-1 rounded">{user?.id}</code>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
